
import { StoreApi } from 'zustand';
import { PriceState } from '../../types';
import { UserState } from '../../../auth/userSlice';
import { priceAlertService } from '@/services/priceAlerts/priceAlertService';

// Dynamic import for emailService to reduce bundle size (~9 KiB savings)
const sendPriceAlertEmailDynamic = async (data: Parameters<typeof import('@/services/email/emailService').sendPriceAlertEmail>[0]) => {
  const { sendPriceAlertEmail } = await import('@/services/email/emailService');
  return sendPriceAlertEmail(data);
};

type GetPriceState = () => PriceState & UserState;
type SetPriceState = StoreApi<PriceState & UserState>["setState"];

export const checkPriceAlerts = async (
  set: SetPriceState,
  get: GetPriceState
) => {
  const state = get();
  const { priceAlerts, currentGoldPrice, currentSilverPrice, user } = state;

  if (!priceAlerts.length || (!currentGoldPrice && !currentSilverPrice)) {
    return;
  }

  // Process each active alert
  for (const alert of priceAlerts) {
    if (!alert.isActive) {
      continue;
    }

    const currentPrice = alert.metal === 'gold' 
      ? currentGoldPrice?.price 
      : currentSilverPrice?.price;

    if (!currentPrice) {
      continue;
    }

    const isTriggered = alert.condition === 'above'
      ? currentPrice >= alert.targetPrice
      : currentPrice <= alert.targetPrice;

    if (isTriggered) {
      const title = "Avviso Prezzo Raggiunto!";
      const message = `Il prezzo ${alert.metal === 'gold' ? "dell'oro" : "dell'argento"} ha raggiunto €${alert.targetPrice.toFixed(2)}`;
      const url = window.location.origin + "/avvisi";
      const triggeredAt = new Date().toISOString();

      try {
        const { notificationService } = await import('@/services/notifications/notificationService');
        await notificationService.sendNotification(title, message, url);
      } catch (notificationError) {
        console.error('[checkPriceAlerts] Error sending notification:', notificationError);
      }

      if (user?.email) {
        try {
          await sendPriceAlertEmailDynamic({
            userEmail: user.email,
            userName: (user as any).user_metadata?.full_name || user.email,
            metal: alert.metal,
            targetPrice: alert.targetPrice,
            currentPrice: currentPrice,
            condition: alert.condition,
            triggeredAt: triggeredAt
          });
          
        } catch (emailError) {
          console.error('[checkPriceAlerts] Error sending price alert email:', emailError);
        }
      }

      // Deactivate the triggered alert in the database
      if (alert.userId) {
        try {
          const success = await priceAlertService.toggleAlert(
            alert.id, 
            alert.userId, 
            false, // isActive = false
            triggeredAt // timestamp when triggered
          );
          
          if (success) {
            // Update local store only after successful DB update
            set((state: PriceState & UserState) => ({
              priceAlerts: state.priceAlerts.map(a => 
                a.id === alert.id 
                  ? { ...a, isActive: false, triggeredAt } 
                  : a
              )
            }));
          } else {
            console.error('[checkPriceAlerts] Failed to update alert in database');
          }
        } catch (dbError) {
          console.error('[checkPriceAlerts] Error updating alert in database:', dbError);
          // Fallback: update local store anyway to prevent repeated notifications
          set((state: PriceState & UserState) => ({
            priceAlerts: state.priceAlerts.map(a => 
              a.id === alert.id ? { ...a, isActive: false } : a
            )
          }));
        }
      } else {
        // For anonymous users, just update local store
        set((state: PriceState & UserState) => ({
          priceAlerts: state.priceAlerts.map(a => 
            a.id === alert.id 
              ? { ...a, isActive: false, triggeredAt } 
              : a
          )
        }));
      }
    }
  }
};
